import { createAsyncThunk } from '@reduxjs/toolkit';

import { removeLegacyAuthCookie } from '@/shared/utils/cookies';
import { startAutoRefresh, stopAutoRefresh } from '@/shared/utils/requestBuilder';
import { removeStoredWorkspaceId } from '@/shared/utils/workspace';
import type {
  LoginCredentials,
  LoginDispatchCredentials,
  LoginDispatchResponse,
  SignupCredentials,
  VerifyTokenCredentials,
} from '@/types/auth/authTypes';
import { authService } from './services';

export const startAuthAutoRefresh = createAsyncThunk('auth/startAutoRefresh', async () => {
  startAutoRefresh();
});

export const stopAuthAutoRefresh = createAsyncThunk('auth/stopAutoRefresh', async () => {
  stopAutoRefresh();
});

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (
    { userId, nomeCompleto }: { userId: string; nomeCompleto: string },
    { rejectWithValue },
  ) => {
    try {
      await authService.updateProfile(userId, { nomeCompleto, perfilCompleto: true });
      return { nomeCompleto };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const requestToken = createAsyncThunk(
  'auth/requestToken',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      return await authService.requestToken(credentials);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const dispatchLogin = createAsyncThunk<
  LoginDispatchResponse,
  LoginDispatchCredentials,
  { rejectValue: string }
>('auth/dispatchLogin', async (credentials, { rejectWithValue }) => {
  try {
    return await authService.dispatchLogin(credentials);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (credentials: VerifyTokenCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.verifyToken(credentials);
      startAutoRefresh();
      return response;
    } catch (error) {
      removeLegacyAuthCookie();
      stopAutoRefresh();
      return rejectWithValue((error as Error).message);
    }
  },
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (sessaoId: string | undefined, { rejectWithValue }) => {
    try {
      stopAutoRefresh();
      await authService.logout(sessaoId);

      removeLegacyAuthCookie();

      if (typeof window !== 'undefined') {
        removeStoredWorkspaceId();
        window.localStorage.removeItem('favWorkspaces');
      }
    } catch (error) {
      stopAutoRefresh();
      removeLegacyAuthCookie();

      if (typeof window !== 'undefined') {
        removeStoredWorkspaceId();
        window.localStorage.removeItem('favWorkspaces');
      }

      return rejectWithValue((error as Error).message);
    }
  },
);

export const verifyLoginToken = createAsyncThunk(
  'auth/verifyLoginToken',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authService.verifyLoginToken(token);
      startAutoRefresh();
      return response;
    } catch (error) {
      removeLegacyAuthCookie();
      stopAutoRefresh();
      return rejectWithValue((error as Error).message);
    }
  },
);

export const loadAuthSession = createAsyncThunk(
  'auth/loadSession',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      startAutoRefresh();
      return { user };
    } catch (error) {
      stopAutoRefresh();
      removeLegacyAuthCookie();
      return rejectWithValue((error as Error).message);
    }
  },
);

export const signup = createAsyncThunk(
  'auth/signup',
  async (credentials: SignupCredentials, { rejectWithValue }) => {
    try {
      return await authService.signup(credentials);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authService.verifyEmail(token);

      if (response.user && response.token) {
        startAutoRefresh();
      }

      return response;
    } catch (error) {
      removeLegacyAuthCookie();
      stopAutoRefresh();
      return rejectWithValue((error as Error).message);
    }
  },
);
